import { AnalyticsService } from 'base/analytics';
import { LibraryCache } from 'base/library_cache';
import { useOnMount } from 'base/useOnMount';
import { BlocksService } from 'blocks/blocks_service';
import { shortcut, useKeyboardShortcuts } from 'blocks/keyboard_shortcuts';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Assistant } from '../ai/assistant';
import { ProjectEditingActions } from '../editing_actions/project';
import { pageAndAllSubpages } from '../model_helpers';
import { SelectionStore } from '../selection/store';
import { SelectionActions } from '../selection_actions';
import { ProjectSession } from '../session/session';
import { populateImagesForPage } from '../styleguide/images';
import {
  StyledElementsContext,
  useLoadConceptFonts,
} from '../styleguide/styled_element_view';
import { WireframePages } from '../wireframe/pages';
import { getGlobalDesignStore } from './design_store';

export const DesignPages = observer(function _DesignPages({
  selection,
  selectionActions,
  analytics,
  session,
  editing,
  library,
  blocksService,
  assistant,
}: {
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  session: ProjectSession;
  editing: ProjectEditingActions | undefined;
  library: LibraryCache;
  blocksService: BlocksService;
  assistant: Assistant;
}) {
  const designStore = getGlobalDesignStore();

  designStore.ensureDesignInitialized();

  const design = designStore.design;
  const concept = design.linkedConcept;

  useKeyboardShortcuts(true, {
    [shortcut('i')]: async () => {
      const imagesToApply = (
        await Promise.all(
          pageAndAllSubpages(session.project.homePage).map((page) =>
            populateImagesForPage(page, session, concept, library, {
              overwrite: page !== session.project.homePage,
            }),
          ),
        )
      ).reduce<Record<string, string>>(
        (acc, images) => ({ ...acc, ...images }),
        {},
      );

      for (const [key, placeholderId] of Object.entries(imagesToApply)) {
        concept.addPlaceholderImage(key, placeholderId);
      }
    },
  });

  useOnMount(() => {
    design.linkedConcept.ensureDesignSchemesSyncedAndApplied(assistant);
  });

  useLoadConceptFonts({ concept, blocksService });

  return (
    <StyledElementsContext concept={concept} library={library}>
      <WireframePages
        selection={selection}
        selectionActions={selectionActions}
        analytics={analytics}
        session={session}
        editing={editing}
        library={library}
        concept={concept}
      />
    </StyledElementsContext>
  );
});
